import {
  createContextualChatSessionContext
} from '../session/liveMode';
import type {
  AvatarContextualChatSeed,
  AvatarLiveFallbackMode,
  AvatarLiveSessionKind,
  AvatarSessionContext
} from '../session/types';
import type { AvatarSuggestionChip, AvatarUrgentInsight } from '../overlay/types';

export interface AvatarHostContext {
  pageId?: string;
  pageTitle?: string;
  route?: string;
  selection?: string;
  metadata?: Record<string, unknown>;
  capabilities?: string[];
}

export type AvatarHostAuthHeaders = Record<string, string>;
export type AvatarHostAuthHeadersResolver =
  () => Promise<AvatarHostAuthHeaders> | AvatarHostAuthHeaders;

export interface AvatarHostLiveAvailability {
  available: boolean;
  reason?: string;
  fallbackMode?: AvatarLiveFallbackMode;
}

export type AvatarHostLiveAvailabilityInput =
  | AvatarHostLiveAvailability
  | boolean;

export type AvatarHostLiveAvailabilityResolver =
  () =>
    Promise<AvatarHostLiveAvailabilityInput>
    | AvatarHostLiveAvailabilityInput;

export interface AvatarHostContextualChatSeed extends AvatarContextualChatSeed {
  hostContext?: AvatarHostContext;
}

export interface AvatarHostLiveConfig {
  enabled?: boolean;
  liveSessionKind: AvatarLiveSessionKind;
  /**
   * Audio transport for the live session. Selects WHO owns the audio leg;
   * the viseme/blendshape sync (`useLiveVisualChannel`) is shared by both.
   *
   * - `'webrtc'` (default): the browser places its OWN WebRTC SIP call and
   *   plays the audio locally. This is the standalone "Live" experience —
   *   browser mic + browser speaker. Requires `liveDestination` +
   *   `sipWebSocketUrl` (else the SDK falls back to browser-only contextual
   *   chat). Honours `liveSessionKind` as configured.
   *
   * - `'voice-call'`: VISUAL-ONLY sync to an EXTERNAL SIP phone call keyed by
   *   `handoffToken`. The avatar lip-syncs the runtime's blendshape stream for
   *   a call whose audio the user hears on their PHONE (or any other SIP leg)
   *   — the browser opens NO WebRTC INVITE, requests NO microphone, and plays
   *   NO local TTS. This is the same engine the Visual IVR runtime host
   *   (`visual.camlinconnect.com.au/visual`) has used in production since
   *   v1.6. Selecting it forces visual-only mode and a non-`contextual-chat`
   *   bootstrap, so the SDK SUBSCRIBES the phone's session via `visual.link`
   *   instead of starting a competing contextual-chat `session.start`
   *   (avoids the `duplicate-session` race). `liveDestination`,
   *   `sipWebSocketUrl`, and `contextualChat` are ignored in this mode — the
   *   external call owns the audio AND the conversation brain.
   *
   * Defaults to `'webrtc'` so existing consumers are unaffected.
   */
  transport?: 'webrtc' | 'voice-call';
  entryUrl?: string;
  /**
   * Optional SIP dial target for hosts that want the real WebRTC/SIP live path.
   * Leave empty for browser-only contextual chat hosts.
   */
  liveDestination?: string;
  /**
   * Optional SIP-over-WebSocket transport override. Hosts may point directly at
   * CamlinVoice (for example wss://sip.<tld>/ws) or at their own proxy.
   */
  sipWebSocketUrl?: string;
  sessionContext?: Partial<AvatarSessionContext>;
  contextualChat?: AvatarHostContextualChatSeed;
  availability?: AvatarHostLiveAvailabilityInput;
  fallbackMode?: AvatarLiveFallbackMode;
  suppressEmbeddedLiveCopy?: boolean;
  handleInputExternally?: boolean;
  /**
   * Open the visual-channel WebSocket as soon as the avatar finishes
   * loading, without waiting for the user to click Listen. Browser
   * security only requires a user gesture for `getUserMedia()` and
   * `audio.play()` — the WebSocket itself can open at any time, and
   * the bot's first prompt + blendshapes can stream in the background
   * so they're ready to play the instant the user does click Listen.
   *
   * Defaults to `false`. Hosts where the avatar IS the page (AgentLink
   * handoff page, Decision Memory full-screen mode) should set this
   * `true`. Builder embeds (Architect Contextual Chat panel, Cara,
   * Cross-Channel Simulator) should leave it `false` so the WS only
   * opens when the panel is actively being used — opening 5 WS
   * connections every time the architect loads would be a waste.
   *
   * Without this flag, contextual-chat hosts hit a "first click silently
   * fails" symptom: the avatar marks ready and dispatches `visual.ready`,
   * but `LiveModeClient.linked` is still false because the WS never
   * opened, so `sendReady()` is no-op'd. The user has to click Listen,
   * wait for the multi-step handshake, then click Listen *again* to
   * actually start talking. Locked down Apr-30 2026 (live-mode
   * reliability fix). Pairs with the `[LiveMode] *` console diagnostics
   * so future regressions on this path surface in seconds.
   */
  autoOpenChannel?: boolean;
  onUnavailable?: (availability: AvatarHostLiveAvailability) => void;
}

export interface AvatarHostActionRequest<TPayload = unknown> {
  type: string;
  payload?: TPayload;
}

export interface AvatarHostActionResult<TResult = unknown> {
  ok: boolean;
  data?: TResult;
  error?: string;
}

export interface AvatarHostEvent<TPayload = unknown> {
  type: string;
  payload?: TPayload;
}

export interface AvatarHostAdapter {
  id?: string;
  getContext?: () => Promise<AvatarHostContext> | AvatarHostContext;
  getAuthHeaders?: AvatarHostAuthHeadersResolver;
  getLiveAvailability?: AvatarHostLiveAvailabilityResolver;
  performAction?: (
    request: AvatarHostActionRequest
  ) => Promise<AvatarHostActionResult> | AvatarHostActionResult;
  subscribe?: (listener: (event: AvatarHostEvent) => void) => (() => void) | void;
  getSuggestions?: (conversationHistory: Array<{role: string, content: string}>) => Promise<{
    suggestions: AvatarSuggestionChip[];
    urgentInsight?: AvatarUrgentInsight;
  }>;
}

export interface CreateStaticHostAdapterOptions {
  id?: string;
  context: AvatarHostContext;
  getAuthHeaders?: AvatarHostAdapter['getAuthHeaders'];
  getLiveAvailability?: AvatarHostAdapter['getLiveAvailability'];
  performAction?: AvatarHostAdapter['performAction'];
  subscribe?: AvatarHostAdapter['subscribe'];
}

export interface CreateHostContextualChatSessionContextOptions {
  sessionContext?: Partial<AvatarSessionContext>;
  hostContext?: AvatarHostContext;
  contextualChat?: AvatarHostContextualChatSeed;
}

export function normalizeAvatarHostLiveAvailability(
  value?: AvatarHostLiveAvailabilityInput
): AvatarHostLiveAvailability | undefined {
  if (typeof value === 'boolean') {
    return { available: value };
  }

  if (!value || typeof value !== 'object') {
    return undefined;
  }

  return {
    available: value.available === true,
    reason: typeof value.reason === 'string' ? value.reason : undefined,
    fallbackMode: value.fallbackMode
  };
}

export function createHostContextualChatSessionContext(
  options: CreateHostContextualChatSessionContextOptions = {}
): AvatarSessionContext {
  const hostContextMetadata = options.hostContext
    ? { hostContext: options.hostContext }
    : {};

  return createContextualChatSessionContext(
    options.sessionContext ?? {},
    {
      ...(options.contextualChat ?? {}),
      metadata: {
        ...(options.contextualChat?.metadata ?? {}),
        ...hostContextMetadata
      }
    }
  );
}

export function createStaticHostAdapter(
  contextOrOptions: AvatarHostContext | CreateStaticHostAdapterOptions,
  performAction?: AvatarHostAdapter['performAction']
): AvatarHostAdapter {
  if (
    contextOrOptions
    && typeof contextOrOptions === 'object'
    && 'context' in contextOrOptions
  ) {
    return {
      id: contextOrOptions.id,
      getContext: () => contextOrOptions.context,
      getAuthHeaders: contextOrOptions.getAuthHeaders,
      getLiveAvailability: contextOrOptions.getLiveAvailability,
      performAction: contextOrOptions.performAction,
      subscribe: contextOrOptions.subscribe
    };
  }

  return {
    getContext: () => contextOrOptions,
    getAuthHeaders: undefined,
    getLiveAvailability: undefined,
    performAction
  };
}
